import { useState } from "react";
import DeviceSyncPanel from "./DeviceSyncPanel";
import StepHeader from "./StepHeader";
import { getDeviceProfile, saveDeviceProfile, type DeviceSenderProfile } from "../lib/device-profile";
import { applyTheme, getSavedTheme, THEME_OPTIONS, type ThemeId } from "../lib/theme";

interface Props {
  onBack: () => void;
  onProfileChanged: (nickname: string) => void;
}

const UK_POSTCODE = /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s?\d[A-Za-z]{2}$/;

function cleanSender(fields: DeviceSenderProfile): DeviceSenderProfile | undefined {
  const sender: DeviceSenderProfile = {
    name: fields.name?.trim() || undefined,
    addressLine1: fields.addressLine1?.trim() || undefined,
    addressLine2: fields.addressLine2?.trim() || undefined,
    townCity: fields.townCity?.trim() || undefined,
    postcode: fields.postcode?.trim().toUpperCase() || undefined,
    mobile: fields.mobile?.trim() || undefined,
    email: fields.email?.trim() || undefined,
  };
  return Object.values(sender).some(Boolean) ? sender : undefined;
}

export default function SettingsPanel({ onBack, onProfileChanged }: Props) {
  const initial = getDeviceProfile();
  const [nickname, setNickname] = useState(initial?.nickname ?? "");
  const [sender, setSender] = useState<DeviceSenderProfile>(initial?.sender ?? {});
  const [theme, setTheme] = useState<ThemeId>(() => getSavedTheme());
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  function updateSender(key: keyof DeviceSenderProfile, value: string) {
    setSender((current) => ({ ...current, [key]: value }));
    setSaved(false);
  }

  function chooseTheme(nextTheme: ThemeId) {
    setTheme(nextTheme);
    applyTheme(nextTheme);
  }

  function save() {
    const trimmedNickname = nickname.trim();
    const nextSender = cleanSender(sender);
    if (!trimmedNickname) {
      setError("Enter the name you'd like us to use.");
      return;
    }
    if (nextSender?.postcode && !UK_POSTCODE.test(nextSender.postcode)) {
      setError("That doesn't look like a valid UK postcode.");
      return;
    }

    saveDeviceProfile({ nickname: trimmedNickname, sender: nextSender });
    setNickname(trimmedNickname);
    setSender(nextSender ?? {});
    setError(null);
    setSaved(true);
    onProfileChanged(trimmedNickname);
  }

  function refreshSyncedProfile() {
    const synced = getDeviceProfile();
    if (!synced) return;
    setNickname(synced.nickname);
    setSender(synced.sender ?? {});
    setSaved(true);
    onProfileChanged(synced.nickname);
  }

  return (
    <main id="main-content" className="page settings-page">
      <div className="stack fade-in-up">
        <div>
          <StepHeader title="Settings" onBack={onBack} />
          <p className="settings-intro">Manage the details this device remembers and choose how the app looks.</p>
        </div>

        <section className="card stack" aria-labelledby="saved-details-heading">
          <div>
            <h3 id="saved-details-heading">Saved address and details</h3>
            <p className="field-help" style={{ margin: 0 }}>
              These are used to pre-fill future postage forms on this device.
            </p>
          </div>

          <div className="field">
            <label htmlFor="settings-nickname">What should we call you?</label>
            <input
              id="settings-nickname"
              value={nickname}
              onChange={(event) => {
                setNickname(event.target.value);
                setSaved(false);
              }}
              autoComplete="given-name"
            />
          </div>
          <div className="field">
            <label htmlFor="settings-name">Full name</label>
            <input id="settings-name" value={sender.name ?? ""} onChange={(event) => updateSender("name", event.target.value)} autoComplete="name" />
          </div>
          <div className="field">
            <label htmlFor="settings-address-1">Address</label>
            <input
              id="settings-address-1"
              value={sender.addressLine1 ?? ""}
              onChange={(event) => updateSender("addressLine1", event.target.value)}
              placeholder="House number and street"
              autoComplete="address-line1"
            />
          </div>
          <div className="field">
            <label htmlFor="settings-address-2">Address line 2 (optional)</label>
            <input
              id="settings-address-2"
              value={sender.addressLine2 ?? ""}
              onChange={(event) => updateSender("addressLine2", event.target.value)}
              autoComplete="address-line2"
            />
          </div>
          <div className="field">
            <label htmlFor="settings-town">Town / city</label>
            <input
              id="settings-town"
              value={sender.townCity ?? ""}
              onChange={(event) => updateSender("townCity", event.target.value)}
              autoComplete="address-level2"
            />
          </div>
          <div className="field">
            <label htmlFor="settings-postcode">Postcode</label>
            <input
              id="settings-postcode"
              value={sender.postcode ?? ""}
              onChange={(event) => updateSender("postcode", event.target.value)}
              placeholder="e.g. SW1A 1AA"
              autoComplete="postal-code"
            />
          </div>
          <div className="field">
            <label htmlFor="settings-mobile">Mobile number</label>
            <input
              id="settings-mobile"
              type="tel"
              value={sender.mobile ?? ""}
              onChange={(event) => updateSender("mobile", event.target.value)}
              autoComplete="tel"
            />
          </div>
          <div className="field">
            <label htmlFor="settings-email">Email (optional)</label>
            <input
              id="settings-email"
              type="email"
              value={sender.email ?? ""}
              onChange={(event) => updateSender("email", event.target.value)}
              autoComplete="email"
            />
          </div>
        </section>

        <section className="card stack-sm" aria-labelledby="appearance-heading">
          <div>
            <h3 id="appearance-heading">Colour theme</h3>
            <p className="field-help" style={{ margin: 0 }}>Your choice applies straight away and is remembered on this device.</p>
          </div>
          <div className="theme-grid" role="radiogroup" aria-label="Colour theme">
            {THEME_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                className="theme-option"
                role="radio"
                aria-checked={theme === option.id}
                onClick={() => chooseTheme(option.id)}
              >
                <span
                  className="theme-preview"
                  style={{ background: `linear-gradient(135deg, ${option.preview[0]}, ${option.preview[1]})` }}
                >
                  <span style={{ background: option.preview[2] }} />
                </span>
                <span className="theme-option-copy">
                  <strong>{option.label}</strong>
                  <small>{option.description}</small>
                </span>
              </button>
            ))}
          </div>
        </section>

        <section aria-labelledby="device-sync-heading" className="stack-sm">
          <div>
            <h3 id="device-sync-heading">Use these details on another device</h3>
            <p className="field-help" style={{ margin: 0 }}>Copy your saved profile with a short-lived, one-time code.</p>
          </div>
          <DeviceSyncPanel onSynced={refreshSyncedProfile} />
        </section>

        {error && <div className="banner banner-danger" role="alert"><span>{error}</span></div>}
        {saved && <div className="banner banner-success" role="status"><span>✓ Your settings are saved.</span></div>}

        <button type="button" className="btn btn-primary" onClick={save}>Save changes</button>
      </div>
    </main>
  );
}
